import path from 'path';

import { TFunction } from 'i18next';

import { ColDef } from '~components/dataGrids/interfaces/colDef';
import { GridColTypeEnum } from '~components/dataGrids/interfaces/gridColTypeEnum';
import { pagePathSegments } from '~constants';
import { CommandTypeEnum, getCommandTypeDisplayName } from '~enums';
import { BaseReference } from '~interfaces';
import i18n from '~lib/i18n';

import TriggerTarget from './triggerTarget';
import TriggerSourceType from '../enum/triggerSourceType';
import TriggerTargetType from '../enum/triggerTargetType';

interface Trigger extends BaseReference {
	type: TriggerSourceType;
	triggerDo: CommandTypeEnum;
	triggerOn: string; // technically an enum, but we don't use it anywhere yet.
	metadata?: number;
	target: TriggerTarget;
	deviceId: string | undefined;
	keyId: string | undefined;
	recurrenceScheduleId: string | undefined;
}

export default Trigger;

export const triggerGridCols: Record<string, ColDef<Trigger>> = {
	deviceOrGroup: {
		field: 'deviceOrGroup',
		headerName: 'entities:device.fields.deviceOrGroup',
		gridColType: GridColTypeEnum.Link,
		addIdToPath: true,
		valueGetter: (value: Trigger) => {
			switch (value.target.type) {
				case TriggerTargetType.DeviceTarget:
					return `${i18n.t('device.labels.single', { ns: 'entities' })} #${value.target.id}`;
				case TriggerTargetType.GroupTarget:
					return `${i18n.t('deviceGroup.labels.single', { ns: 'entities' })} #${value.target.id}`;
				default:
					return undefined;
			}
		},
		linkPathGetter: (value: Trigger) => {
			switch (value.target.type) {
				case TriggerTargetType.DeviceTarget:
					return path.join(pagePathSegments.Devices, ':id', pagePathSegments.General);
				case TriggerTargetType.GroupTarget:
					return path.join(pagePathSegments.DeviceGroups, ':id');
				default:
					return '';
			}
		},
		linkPathIdGetter: (value: Trigger) => value.target?.id,
		defaultShown: true,
	},
	triggerAction: {
		field: 'triggerDo',
		headerName: 'entities:action.labels.single',
		valueGetter: (trigger: Trigger) => {
			const commandType = trigger.triggerDo as CommandTypeEnum;
			const action = getCommandTypeDisplayName(commandType, i18n.t);
			const metadataMinutes = Number(trigger.metadata);
			if (
				Number.isFinite(metadataMinutes) &&
				metadataMinutes > 0 &&
				(commandType === CommandTypeEnum.OfficeMode ||
					commandType === CommandTypeEnum.EnableOfficeMode)
			) {
				return `${action}, ${formatOfficeModeMetadataDuration(metadataMinutes, i18n.t)}`;
			}
			return action;
		},
		defaultShown: true,
	},
};

// This should probably be some util function elsewhere. It didn't belong in useColDefs at least.
const formatOfficeModeMetadataDuration = (metadataMinutes: number, t: TFunction): string => {
	const andLabel = t('and', { ns: 'system' });
	const totalMinutes = Math.max(0, Math.floor(metadataMinutes));
	const days = Math.floor(totalMinutes / (24 * 60));
	const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
	const minutes = totalMinutes % 60;

	if (totalMinutes < 60) {
		return t('minute.minutes', {
			ns: 'system',
			count: totalMinutes,
		});
	}

	if (totalMinutes <= 24 * 60) {
		return `${t('hour.hours', {
			ns: 'system',
			count: hours,
		})} ${andLabel} ${t('minute.minutes', {
			ns: 'system',
			count: minutes,
		})}`;
	}

	return `${t('day.days', {
		ns: 'system',
		count: days,
	})}, ${t('hour.hours', {
		ns: 'system',
		count: hours,
	})} ${andLabel} ${t('minute.minutes', {
		ns: 'system',
		count: minutes,
	})}`;
};
